
import { User, Course } from "@/types";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { toast } from "@/hooks/use-toast";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";
import { MapPin, Loader2, LogOut } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { updateProfile, setHomeCourse, addCourseToUser } from "@/integrations/supabase/profileCourseService";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";
import CourseSelector from "@/components/CourseSelector";

interface ProfileViewProps {
  user: User;
  homeCourseId: string | null;
}

const ProfileView = ({ user, homeCourseId }: ProfileViewProps) => {
  const { signOut } = useAuth();
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email || "");
  const [isSaving, setIsSaving] = useState(false);
  const [homeCourseName, setHomeCourseName] = useState<string | null>(null);
  const [showCourseDialog, setShowCourseDialog] = useState(false);

  useEffect(() => {
    if (!homeCourseId) return;

    const fetchCourseName = async () => {
      const { data: course } = await supabase
        .from('courses')
        .select('name')
        .eq('id', homeCourseId)
        .single();

      if (course) setHomeCourseName(course.name);
    };

    fetchCourseName();
  }, [homeCourseId]);

  const handleSaveProfile = async () => {
    setIsSaving(true);
    try {
      const result = await updateProfile(user.id, { name, email });
      if (result.error) throw result.error;

      toast({
        title: "Profile Updated",
        description: "Your profile information has been saved.",
      });
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: "Error",
        description: "Failed to save profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangeHomeCourse = async (course: Course) => {
    try {
      await addCourseToUser(user.id, course.id, true);
      const result = await setHomeCourse(user.id, course.id);
      if (result.error) throw result.error;

      setHomeCourseName(course.name);
      setShowCourseDialog(false);
      toast({
        title: "Home Course Updated",
        description: `${course.name} is now your home course.`,
      });
    } catch (error) {
      console.error("Error changing home course:", error);
      toast({
        title: "Error",
        description: "Failed to update home course. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="px-4 pb-20">
      <div className="flex flex-col items-center justify-center py-6">
        <AvatarPlaceholder user={user} size="lg" className="border-2 border-gold/40" />
      </div>

      <div className="space-y-6 mt-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {/* Home Course */}
        <div className="space-y-2">
          <Label>Home Course</Label>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-secondary rounded-md min-h-[40px]">
              <MapPin className="h-4 w-4 text-forest shrink-0" />
              <span className="text-sm truncate">
                {homeCourseName || "Not set"}
              </span>
            </div>
            <Dialog open={showCourseDialog} onOpenChange={setShowCourseDialog}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  Change
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Change Home Course</DialogTitle>
                </DialogHeader>
                <CourseSelector onSelectCourse={handleChangeHomeCourse} />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <Button
          className="w-full bg-forest hover:bg-forest/90 text-white"
          onClick={handleSaveProfile}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Profile"
          )}
        </Button>

        <div className="pt-4 border-t">
          <Button
            variant="outline"
            className="w-full text-destructive hover:text-destructive"
            onClick={signOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProfileView;
